<template>
  <StaffLayout section="Video Library">
    <div v-if="isLoading"><Loading /></div>
    <div v-show="!isLoading">
      <div class="pb-4">
        <BackButton to="../videos"> Back to Videos List </BackButton>
      </div>
      <h1 class="py-3 mb-6 border-b flex">
        <div class="text-xl flex-grow">
          <div class="font-bold">Edit Video</div>
          <div>
            {{ video?.fileName }}
          </div>
        </div>
        <EditButton @click="editVideo">Edit</EditButton>
      </h1>
      <div class="mt-4">
        <h1 class="font-bold">Label</h1>
        <p>{{ video?.label }}</p>
      </div>
      <section class="mt-4 video">
        <h1 class="font-bold mb-2">Video Preview</h1>
        <div class="video-container">
          <video
            ref="videoplayer"
            id="azuremediaplayer"
            class="azuremediaplayer amp-default-skin amp-big-play-centered"
            tabindex="0"
          >
            <p class="amp-no-js">
              To view this video please enable JavaScript, and consider upgrading to a web browser
              that supports HTML5 video
            </p>
          </video>
          <div class="w-full" id="videoplaceholder"></div>
        </div>
      </section>
      <div class="mt-4">
        <div class="font-bold">Portals That Reference This Video</div>
        <div v-if="referencingPortals?.length == 0">None.</div>
        <div v-else>
          <router-link
            v-for="portal in referencingPortals"
            class="block underline mb-1 w-max"
            :to="`/staff/${portal?.siteCode}/prepare`"
          >
            {{ portal?.clientName }} > {{ portal?.name }} ({{ portal?.siteCode }})
          </router-link>
        </div>
      </div>
    </div>
    <EditPopup
      entity-name="Video"
      :is-visible="isPopupVisible"
      :is-edit-mode="true"
      :save-error="saveError"
      :delete-enabled="true"
      @delete="deleteVideo"
      @close="isPopupVisible = false"
      @save="startSave"
    >
      <BaseInput label="File Name" v-model="fileName" type="text" :error="errors.fileName" />
      <BaseInput label="Label" v-model="label" type="text" :error="errors.label" />
      <BaseInput label="Stream Path" v-model="streamPath" type="text" :error="errors.streamPath" />
      <div class="mt-3" />
      <BaseCheckbox label="Is Full Video" v-model="isFullVideo" />
      <div class="mt-5 border-b mb-4" />
      <BaseInput
        label="English Captions URL"
        v-model="captionsUrlEn"
        type="text"
        :error="errors.captionsUrlEn"
      />
      <BaseInput
        label="Spanish Captions URL"
        v-model="captionsUrlEs"
        type="text"
        :error="errors.captionsUrlEs"
      />
    </EditPopup>
  </StaffLayout>
</template>

<script lang="ts" setup>
import Player, { VimeoEmbedParameters } from "@vimeo/player";
import { useAdminStore } from "../../store/adminStore";
import { useBldgStore } from "../../store/bldgStore";

import { useField, useForm } from "vee-validate";
import * as yup from "yup";
import { SaveVideoRequest } from "../../store/generatedFetchClient";

const bldgStore = useBldgStore();
const adminStore = useAdminStore();
const router = useRouter();
const route = useRoute();
let isLoading = ref(true);

const videoplayer = ref(null);
var vimeoPlayer: Player = null;

let video = computed(() => adminStore.selectedVideo);
let videoSrc = ref("");
let referencingPortals = computed(() =>
  video.value?.portalIds
    ?.map((id) => bldgStore.properties.find((p) => p.portalId == id))
    .sort((a, b) =>
      a.clientName == b.clientName
        ? a.name.localeCompare(b.name)
        : a.clientName.localeCompare(b.clientName)
    )
);

onMounted(loadData);
async function loadData() {
  isLoading.value = true;
  if (route.params.videoId && typeof route.params.videoId === "string") {
    await bldgStore.getPropertyList();
    await adminStore.getVideo(Number.parseInt(route.params.videoId));

    let vidUrl = video.value?.streamPath ?? "";

    // Switch to secure protocol on production
    if (location.protocol === "https:") {
      vidUrl = vidUrl.replace("http:", "https:");
    }

    videoSrc.value = vidUrl;
    isLoading.value = false;
    if (videoSrc.value.includes("vimeo")) {
      playVimeoVideo();
    }
  }
  isLoading.value = false;
}

onUnmounted(removePlayer);
function removePlayer() {
  vimeoPlayer.destroy();
}

function playVimeoVideo() {
  console.log("Playing Vimeo Video");
  (document.querySelector("#azuremediaplayer") as HTMLElement).hidden = true;

  var options = {
    url: videoSrc.value,
    autoplay: false,
    responsive: true,

    vimeo_logo: false,
    fullscreen: false,
    loop: false,
    airplay: false,
    pip: false,
    quality_selector: false,
    transcript: false,
    title: false,
  } as VimeoEmbedParameters;
  vimeoPlayer = new Player("videoplaceholder", options);

  vimeoPlayer.on("play", function () {
    console.log("Played the video");
  });

  vimeoPlayer.getVideoTitle().then(function (title) {
    console.log("title:", title);
  });

  vimeoPlayer.on("ended", function () {
    console.log("Vimeo Video ended playback");
  });
}

// Popup stuff
let saveError = ref("");
let isPopupVisible = ref(false);

const { handleSubmit, errors } = useForm({
  validationSchema: yup.object({
    fileName: yup.string().required().max(512).label("File Name"),
    label: yup.string().required().max(50).label("Label"),
    streamPath: yup.string().required().max(512).label("Stream Path"),
    captionsUrlEn: yup.string().notRequired().max(512).label("English Captions URL"),
    captionsUrlEs: yup.string().notRequired().max(512).label("Spanish Captions URL"),
  }),
  initialValues: {
    fileName: "",
    label: "",
    streamPath: "",
    captionsUrlEn: "",
    captionsUrlEs: "",
  },
});

const { value: fileName } = useField<string>("fileName");
const { value: label } = useField<string>("label");
const { value: streamPath } = useField<string>("streamPath");
let isFullVideo = ref(true);
const { value: captionsUrlEn } = useField<string>("captionsUrlEn");
const { value: captionsUrlEs } = useField<string>("captionsUrlEs");

let startSave = handleSubmit(onValidSubmit, onInvalidSubmit);

async function onValidSubmit() {
  saveError.value = "";

  // Clone object before updating
  let v = { ...video.value };

  if (v) {
    v.fileName = fileName.value;
    v.label = label.value;
    v.streamPath = streamPath.value;
    v.isFullVideo = isFullVideo.value;
    v.captionsUrlEn = captionsUrlEn.value;
    v.captionsUrlEs = captionsUrlEs.value;

    saveError.value = await adminStore.saveVideo(v as SaveVideoRequest);
    if (saveError.value) return;

    adminStore.selectedVideo = v;
    adminStore.videos = []; // Invalidate video list to force reload next time
    isPopupVisible.value = false;
  }
}

function onInvalidSubmit(ctx: any) {
  saveError.value = "One or more invalid form inputs.";
}

function editVideo() {
  fileName.value = video.value.fileName ?? "";
  label.value = video.value.label ?? "";
  streamPath.value = video.value.streamPath ?? "";
  isFullVideo.value = video.value.isFullVideo ?? true;
  captionsUrlEn.value = video.value.captionsUrlEn ?? "";
  captionsUrlEs.value = video.value.captionsUrlEs ?? "";

  saveError.value = "";
  isPopupVisible.value = true;
}

async function deleteVideo() {
  saveError.value = await adminStore.deleteVideo(video.value.videoId);
  if (saveError.value) return;

  adminStore.videos = []; // Invalidate video list to force reload next time
  router.push(`../videos`);
}
</script>
